import React from 'react';

interface ChecklistRendererProps {
  content: string;
  onUpdate?: (newContent: string) => void;
  readOnly?: boolean;
}

export const ChecklistRenderer: React.FC<ChecklistRendererProps> = ({
  content,
  onUpdate,
  readOnly = false
}) => {
  if (!content) return null;

  // 解析内容，识别 checklist 项和普通文本
  const lines = content.split('\n');
  const parsedLines = lines.map((line, index) => {
    // 匹配 Markdown checklist 格式: [ ] 或 [x] （可选的 - 前缀）
    // 支持: [ ], -[ ], - [ ], [], -[], - 内容
    const checkboxMatch = line.match(/^(\s*)-?\s*\[\s*([ xX]?)\s*\]\s*(.*)$/);
    // 匹配简化格式: -内容 或 - 内容（以单个 - 开头的行也视为未选中的checklist）
    // 排除 Markdown 水平线: ---, ----, 等
    const isHorizontalRule = line.match(/^(\s*)-{2,}/);
    const dashMatch = !isHorizontalRule ? line.match(/^(\s*)-\s*(.+)$/) : null;

    if (checkboxMatch) {
      const indent = checkboxMatch[1];
      const checked = checkboxMatch[2].toLowerCase() === 'x';
      const text = checkboxMatch[3] || ''; // 允许空文本

      return {
        type: 'checklist' as const,
        lineIndex: index,
        checked,
        text,
        indent,
        originalLine: line
      };
    } else if (dashMatch) {
      // 简化格式：- 内容 => 视为未选中的checklist
      return {
        type: 'checklist' as const,
        lineIndex: index,
        checked: false,
        text: dashMatch[2],
        indent: dashMatch[1],
        originalLine: line
      };
    }

    return {
      type: 'text' as const,
      lineIndex: index,
      content: line
    };
  });

  // 切换 checkbox 状态
  const toggleCheckbox = (lineIndex: number) => {
    if (readOnly || !onUpdate) return;

    const newLines = [...lines];
    const line = newLines[lineIndex];

    // 处理简化格式: -内容 或 - 内容
    // 排除 Markdown 水平线
    const dashMatch = line.match(/^(\s*)-\s*(.+)$/);
    if (dashMatch && !line.match(/\[/) && !line.match(/^(\s*)-{2,}/)) {
      // 简化格式未选中 -> 选中
      newLines[lineIndex] = `${dashMatch[1]}[x] ${dashMatch[2]}`;
      onUpdate(newLines.join('\n'));
      return;
    }

    // 切换 [x] 和 [ ] （更宽松的替换，支持可选的 - 前缀）
    if (line.match(/-?\s*\[\s*\]/)) {
      // 未选中 -> 选中
      newLines[lineIndex] = line.replace(/-?\s*\[\s*\]/, '[x]');
    } else if (line.match(/-?\s*\[\s*[xX]\s*\]/)) {
      // 选中 -> 未选中
      newLines[lineIndex] = line.replace(/-?\s*\[\s*[xX]\s*\]/, '[ ]');
    }

    onUpdate(newLines.join('\n'));
  };

  return (
    <div className="checklist-container">
      {/* 渲染内容 */}
      <div className="space-y-1">
        {parsedLines.map((item, idx) => {
          if (item.type === 'checklist') {
            return (
              <div
                key={idx}
                className={`flex items-start gap-2 ${item.indent ? 'pl-4' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => toggleCheckbox(item.lineIndex)}
                  disabled={readOnly}
                  className={`
                    flex-shrink-0 mt-0.5 w-4 h-4 rounded border-2 transition-all
                    ${item.checked
                      ? 'bg-green-500 border-green-500'
                      : 'bg-white border-gray-300 hover:border-green-400'
                    }
                    ${readOnly ? 'cursor-default' : 'cursor-pointer'}
                    flex items-center justify-center
                  `}
                >
                  {item.checked && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                  )}
                </button>
                <span
                  className={`
                    text-sm leading-relaxed
                    ${item.checked
                      ? 'line-through text-gray-500'
                      : 'text-gray-700'
                    }
                  `}
                >
                  {item.text}
                </span>
              </div>
            );
          } else {
            // 普通文本行
            if (item.content.trim() === '') {
              return <div key={idx} className="h-4" />; // 空行
            }
            return (
              <p key={idx} className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {item.content}
              </p>
            );
          }
        })}
      </div>
    </div>
  );
};
